// import { useStorage } from "@vueuse/core";
import { printHelperPorts } from "@mono/constants/lib/printHelperPorts";

const basePrintHelperUrl = "http://localhost";

const bucketUrl =
  "https://mezan-print-helper-releases.s3.eu-west-1.amazonaws.com/releases";
const LATEST_SEMVER_VERSION = "2.0.1";

const printHelperAppDownloadUrls = {
  development: `${bucketUrl}/development/Mezan+Print+Helper+Development+v${LATEST_SEMVER_VERSION}+Installer.exe`,
  staging: `${bucketUrl}/staging/Mezan+Print+Helper+Staging+v${LATEST_SEMVER_VERSION}+Installer.exe`,
  production: `${bucketUrl}/live/Mezan+Print+Helper+v${LATEST_SEMVER_VERSION}+Installer.exe`,
};

const url = ref<string>();
const loading = ref(false);
const isConnected = ref(false);
const currentAppVersion = ref<string>();

export interface PrinterType {
  name: string;
  displayName: string;
  description: string;
  status: number;
  isDefault: boolean;
  options: {
    "printer-location": string;
    "printer-make-and-model": string;
    system_driverinfo: string;
  };
}

export const usePrintHelper = () => {
  const config = useRuntimeConfig();

  // computed
  const showDownloadBtn = computed(
    () =>
      isConnected.value === false ||
      currentAppVersion.value !== LATEST_SEMVER_VERSION
  );

  const resetConnection = () => {
    url.value = undefined;
    isConnected.value = false;
    currentAppVersion.value = undefined;
  };

  const tryConnect = async () => {
    try {
      const promises = printHelperPorts.map(async (port) => {
        const response = await fetch(`${basePrintHelperUrl}:${port}`, {
          method: "POST",
        });

        if (!response.ok) {
          throw new Error("Network Error");
        }

        const data = (await response.json()) as {
          msg: string;
          live: boolean;
          version: string;
        };

        return { port, data };
      });

      const { port, data } = await Promise.any(promises);

      url.value = `${basePrintHelperUrl}:${port}`;
      currentAppVersion.value = data.version;
      isConnected.value = true;
      loading.value = false;
    } catch {
      // empty catch
      resetConnection();
    }
  };

  const preloadPrintHelper = async () => {
    if (url.value == null) {
      return;
    }

    const { getTestPrintData } = usePrint();
    const { url: printUrl, posLogoUrl } = getTestPrintData();

    try {
      const response = await fetch(url.value, {
        method: "POST",
        body: JSON.stringify({
          printUrl,
          posLogoUrl,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Network Error");
      }
    } catch {
      resetConnection();
    }
  };

  const silentPrint = async ({
    printInvoice,
    printBaseUrl,
  }: {
    printInvoice: any;
    printBaseUrl: string;
  }) => {
    if (url.value == null) {
      return;
    }

    const terminalStore = useTerminalStore();

    try {
      let printers = JSON.parse(
        JSON.stringify(terminalStore.options.printers)
      ) as typeof terminalStore.options.printers;

      while (printers.length > 0) {
        const response = await fetch(`${url.value}/print`, {
          method: "POST",
          body: JSON.stringify({
            printUrl: printBaseUrl,
            devices: printers.map((printer) => ({
              name: printer.name,
              copies: 1,
            })),
            printData: printInvoice,
          }),
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error("Network Error");
        }

        printers = printers.filter((printer) => {
          --printer.copies;

          return printer.copies > 0;
        });
      }
    } catch {
      resetConnection();
    }
  };

  const getPrinterList = async (): Promise<PrinterType[]> => {
    if (url.value == null) {
      return [];
    }

    try {
      const response = await fetch(`${url.value}/printers`);

      if (!response.ok) {
        return [];
      }

      return response.json();
    } catch {
      resetConnection();
      return [];
    }
  };

  const signInvoice = async (
    data: any
  ): Promise<
    | {
        qr: string;
        invoice_hash: string;
        signed_invoice_string: string;
        egsInfoUuid: string;
      }
    | undefined
  > => {
    if (url.value == null) {
      return;
    }

    try {
      const response = await fetch(`${url.value}/signinvoice`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        return;
      }

      return response.json();
    } catch {
      resetConnection();
    }
  };

  return {
    async init() {
      await tryConnect();
    },
    downloadUrl:
      printHelperAppDownloadUrls[
        config.public.API_TARGET as keyof typeof printHelperAppDownloadUrls
      ],
    isConnected,
    tryConnect,
    preloadPrintHelper,
    loading,
    showDownloadBtn,
    silentPrint,
    getPrinterList,
    signInvoice,
  };
};
